/* global gdAlertify */
// En el archivo viejo este modal era una closure anidada dentro del
// componente CalendarioTurnos. Se extrae aquí tal cual: no dependía de nada
// del scope del padre aparte de sus props (isOpen, onClose, onConfirm, row,
// periodo) y del hook useUmbralesValores() propio.
import { useState, useEffect } from "react";
import axiosInstance from "../../../config/axiosConfig";
import InfoHeader from "./InfoHeader";
import {
    AlertaUmbrales,
    confirmarSiExcedeUmbral,
    useUmbralesValores,
} from "../../../utils/umbralesValores";
import { getValorTurnoActual } from "../helpers";

const ActualizarValoresModal = ({ isOpen, onClose, onConfirm, row, periodo }) => {
    const [tipoActualizacion, setTipoActualizacion] = useState("individual"); // individual | rango
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFin, setFechaFin] = useState("");
    const [periodoDesde, setPeriodoDesde] = useState("");
    const [periodoHasta, setPeriodoHasta] = useState("");
    const [valorTurno, setValorTurno] = useState("");
    const [valorColacion, setValorColacion] = useState("");
    const [valorLocomocion, setValorLocomocion] = useState("");
    const [valorOtro, setValorOtro] = useState("");
    const [tipoTurno, setTipoTurno] = useState("1");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const umbrales = useUmbralesValores();
    const valoresUmbral = {
        turno: valorTurno,
        colacion: valorColacion,
        movilizacion: valorLocomocion,
    };


    // Inicializa los valores cuando se abre
    useEffect(() => {
        if (isOpen && row) {
            setValorTurno(getValorTurnoActual(row) || "");
            setValorColacion(row.valorColacionRt || row.reemplazoValorColacion || row.valorColacion || "");
            setValorLocomocion(row.valorLocomocionRt || row.reemplazoValorMovilizacion || row.valorMovilizacion || "");
            setValorOtro(row.valorOtroRt || row.reemplazoValorOtro || row.valorOtro || "");
            setTipoTurno(row.tipo === "day" ? "1" : "2");
            setTipoActualizacion("individual");
            setFechaInicio(row.fecha || "");
            setFechaFin("");
        }
    }, [isOpen, row]);

    // Calcula el rango del período
    useEffect(() => {
        if (!periodo) return;
        const [anioStr, mesStr] = periodo.split("-");
        const anio = parseInt(anioStr, 10);
        const mes = parseInt(mesStr, 10);
        const desde = new Date(anio, mes - 2, 26);
        const hasta = new Date(anio, mes - 1, 25);
        setPeriodoDesde(desde.toISOString().split("T")[0]);
        setPeriodoHasta(hasta.toISOString().split("T")[0]);
    }, [periodo]);

    if (!isOpen || !row) return null;

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validaciones
        if (tipoActualizacion === "rango") {
            if (!fechaInicio || !fechaFin) {
                gdAlertify.notiError("Debe ingresar fechas inicio y fin");
                return;
            }
            if (fechaInicio > fechaFin) {
                gdAlertify.notiError("Fecha inicio no puede ser mayor que la de fin");
                return;
            }
            if (fechaInicio < periodoDesde) {
                gdAlertify.notiError(`Las fechas deben ser mayores a ${periodoDesde}`);
                return;
            }
        }

        // Aviso si algún monto supera el umbral configurado (no impide guardar).
        confirmarSiExcedeUmbral({
            valores: valoresUmbral,
            umbrales,
            onCancel: () => gdAlertify.notify("Cancelado", "error"),
            onConfirm: () => {
                if (tipoActualizacion === "rango") {
                    // Confirmación previa de rango
                    gdAlertify.confirm({
                        title: "Confirmar Actualización por Rango",
                        message: `⚠️ Esta acción reemplazará todos los valores entre ${fechaInicio} y ${fechaFin}. ¿Desea continuar?`,
                        onConfirm: () => handleConfirmGuardar(true),
                        onCancel: () => gdAlertify.notify("Cancelado", "error"),
                    });
                } else {
                    handleConfirmGuardar(false);
                }
            },
        });
    };

    const handleConfirmGuardar = async (esRango) => {
        gdAlertify.confirm({
            title: "Actualizar Valores",
            message: esRango
                ? "¿Está seguro de actualizar los valores en el rango seleccionado?"
                : "¿Está seguro de actualizar los valores?",
            onConfirm: async () => {
                setIsSubmitting(true);
                gdAlertify.showLoader();
                try {
                    const payload = {
                        pacienteID: row.pacienteID,
                        auxiliarID: row.auxiliarID,
                        fechaInicio: esRango ? fechaInicio : row.fecha,
                        fechaFin: esRango ? fechaFin : row.fecha,
                        periodo: row.tipo,
                        tipoTurno,
                        tipoActualizacion, // "individual" | "rango"
                        valorTurno,
                        valorColacion,
                        valorLocomocion,
                        valorOtro,
                        es_apoyo: row.es_apoyo,
                    };

                    const resp = await axiosInstance.post("/turnos/valores/actualiza", payload);

                    if (!resp.data.error) {
                        gdAlertify.notiSuccess(resp.data.message || "Valores actualizados");
                        onConfirm({ pacienteID: row.pacienteID, fecha: row.fecha, esRango });
                        onClose();
                    } else {
                        gdAlertify.notiError(resp.data.message || "No se pudo actualizar");
                    }
                } catch (err) {
                    gdAlertify.notiError("Error al conectar con el servidor.");
                } finally {
                    gdAlertify.hideLoader();
                    setIsSubmitting(false);
                }
            },
            onCancel: () => gdAlertify.notify("Cancelado", "error"),
        });
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <form onSubmit={handleSubmit}>
                            <div className="modal-header">
                                <h5 className="modal-title">Actualizar Valores</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="modal-body">
                                <InfoHeader row={row} />

                                <AlertaUmbrales valores={valoresUmbral} umbrales={umbrales} className="mt-2" />

                                {/* Tipo de actualización */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Tipo de actualización</label>
                                    <select
                                        className="form-select"
                                        value={tipoActualizacion}
                                        onChange={(e) => setTipoActualizacion(e.target.value)}
                                        disabled={isSubmitting}
                                    >
                                        <option value="individual">
                                            Fecha seleccionada ({row.fecha})
                                        </option>
                                        <option value="rango">Rango de fechas</option>
                                    </select>
                                </div>

                                {/* Fechas si es rango */}
                                {tipoActualizacion === "rango" && (
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Fecha Inicio</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaInicio}
                                                onChange={(e) => setFechaInicio(e.target.value)}
                                                min={periodoDesde}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Fecha Fin</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fechaFin}
                                                onChange={(e) => setFechaFin(e.target.value)}
                                                min={periodoDesde}
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <p className="text-muted mt-2">
                                            Periodo válido: {periodoDesde} al {periodoHasta}
                                        </p>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Tipo Turno</label>
                                    <select
                                        className="form-select"
                                        value={tipoTurno}
                                        onChange={(e) => setTipoTurno(e.target.value)}
                                        disabled={isSubmitting}
                                    >
                                        <option value={row.tipo === "day" ? "1" : "2"}>
                                            Actual - {row.tipo === "day" ? "Día" : "Noche"}
                                        </option>
                                        <option value="3">Día y noche</option>
                                    </select>
                                </div>

                                {/* Sección de valores */}
                                <div className="row g-3">
                                    <div className="col-md-12 mb-3 text-center"><b>Se actualiza el valor para todos los turnos entre la fecha seleccionada</b></div>
                                    <div className="col-md-6">
                                        <div className="card p-3 shadow-sm border-0 bg-light">
                                            <label className="form-label mb-1 fw-semibold text-muted">Valor Turno</label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={valorTurno}
                                                    onChange={(e) => setValorTurno(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="card p-3 shadow-sm border-0 bg-light">
                                            <label className="form-label mb-1 fw-semibold text-muted">Colación</label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={valorColacion}
                                                    onChange={(e) => setValorColacion(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="card p-3 shadow-sm border-0 bg-light">
                                            <label className="form-label mb-1 fw-semibold text-muted">Locomoción</label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={valorLocomocion}
                                                    onChange={(e) => setValorLocomocion(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="card p-3 shadow-sm border-0 bg-light">
                                            <label className="form-label mb-1 fw-semibold text-muted">Otros</label>
                                            <div className="input-group">
                                                <span className="input-group-text">$</span>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={valorOtro}
                                                    onChange={(e) => setValorOtro(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Guardando..." : "Guardar"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ActualizarValoresModal;
